// Aerator Project - DVP&R Test Seed Data
// Design Verification Plan & Report - Complete Test Results Across 17 Iterations

export interface TestSpecification {
    testId: string;
    testName: string;
    testType: 'Reliability' | 'Performance' | 'Safety' | 'Environmental' | 'Regulatory' | 'Material';
    testStandard: string;
    linkedFmeaIds: string[];
    linkedBomIds: string[];
    objectiveStatement: string;
    testMethod: string;
    sampleSize: number;
    testDuration: string;
    testEquipment: string[];
    testEnvironment: {
        temperature?: string;
        humidity?: string;
        other?: string;
    };
}

export interface TestParameter {
    parameterId: string;
    testId: string;
    parameterName: string;
    unit: string;
    nominalValue: number | string;
    lowerLimit: number | string;
    upperLimit: number | string;
    criticalParameter: boolean;
}

export interface PassFailCriteria {
    criteriaId: string;
    testId: string;
    description: string;
    acceptanceCriteria: string;
    rejectCriteria: string;
    quantitativeLimit?: {
        parameter: string;
        operator: '<' | '>' | '<=' | '>=' | '==' | 'range';
        value: number | string;
    };
}

export interface TestResult {
    resultId: string;
    testId: string;
    iterationNumber: number;
    iterationDate: string;
    testDate: string;
    testedBy: string; // Initials
    witnessedBy?: string; // Initials
    sampleId: string;
    status: 'Pass' | 'Fail' | 'Conditional' | 'Not Tested';
    measurements: {
        parameterId: string;
        measuredValue: number | string;
        passFail: 'Pass' | 'Fail';
    }[];
    overallResult: 'Pass' | 'Fail' | 'Conditional';
    failureMode?: string;
    rootCause?: string;
    correctiveAction?: string;
    notes?: string;
    testReportPath?: string;
    projectName?: string;
}

// ============================================
// TEST SPECIFICATIONS
// ============================================

export const testSpecifications: TestSpecification[] = [
    {
        testId: 'TEST-001',
        testName: 'Drop Test - Housing Integrity',
        testType: 'Reliability',
        testStandard: 'IEC 60068-2-31',
        linkedFmeaIds: ['FMEA-001'],
        linkedBomIds: ['2', '9'],
        objectiveStatement: 'Verify housing withstands 1m drop onto concrete without cracking or failure',
        testMethod: 'Free-fall drop test from 1 meter height onto concrete floor, 3-axis (6 orientations)',
        sampleSize: 5,
        testDuration: '1 hour (setup + execution)',
        testEquipment: ['Drop Test Fixture', 'Concrete Surface (minimum 50mm thick)', 'Measuring Tape'],
        testEnvironment: {
            temperature: '23°C ± 2°C',
            humidity: '50% ± 10%'
        }
    },
    {
        testId: 'TEST-002',
        testName: 'Shaft Seal Leak Test - Hot Liquid',
        testType: 'Reliability',
        testStandard: 'Internal Test Protocol ITP-SEAL-001',
        linkedFmeaIds: ['FMEA-016'],
        linkedBomIds: ['15', '16', '17'],
        objectiveStatement: 'Verify shaft seal prevents liquid ingress when immersed in 80°C liquid for 30 minutes',
        testMethod: 'Immerse assembled whisk in 80°C water for 30 minutes while motor is running at 15000 RPM. Check for liquid ingress.',
        sampleSize: 10,
        testDuration: '45 minutes per sample',
        testEquipment: ['Hot Water Bath', 'Thermometer (calibrated)', 'Leak Detection Kit'],
        testEnvironment: {
            temperature: 'Ambient',
            other: 'Test liquid at 80°C ± 2°C'
        }
    },
    {
        testId: 'TEST-003',
        testName: 'Wire Flex Fatigue Test',
        testType: 'Reliability',
        testStandard: 'UL 1026',
        linkedFmeaIds: ['FMEA-003'],
        linkedBomIds: ['12'],
        objectiveStatement: 'Verify wire and strain relief withstand 10,000 flex cycles without failure',
        testMethod: 'Flex wire at exit point through 90° arc at 30 cycles/minute for 10,000 cycles. Monitor for breaks.',
        sampleSize: 3,
        testDuration: '6 hours per sample',
        testEquipment: ['Flex Test Machine', 'Continuity Tester'],
        testEnvironment: {
            temperature: '23°C ± 5°C'
        }
    },
    {
        testId: 'TEST-004',
        testName: 'Switch Ingress Protection (IPX4)',
        testType: 'Safety',
        testStandard: 'IEC 60529 IPX4',
        linkedFmeaIds: ['FMEA-008'],
        linkedBomIds: ['8', '10'],
        objectiveStatement: 'Verify switch assembly is protected against water splash from any direction',
        testMethod: 'Spray water from all directions using oscillating spray head for 5 minutes minimum',
        sampleSize: 3,
        testDuration: '15 minutes per sample',
        testEquipment: ['IPX4 Spray Test Equipment', 'Water Supply', 'Spray Nozzle'],
        testEnvironment: {
            temperature: '15-35°C',
            other: 'Water temperature 15°C ± 10°C'
        }
    },
    {
        testId: 'TEST-005',
        testName: 'Motor Brush Life Test',
        testType: 'Reliability',
        testStandard: 'Internal Test Protocol ITP-MOT-001',
        linkedFmeaIds: ['FMEA-011'],
        linkedBomIds: ['6'],
        objectiveStatement: 'Verify motor brushed DC motor operates for minimum 100 hours without brush wear failure',
        testMethod: 'Run motor continuously at rated speed (12000 RPM) for 100 hours, monitor brush wear',
        sampleSize: 5,
        testDuration: '100 hours (4.2 days)',
        testEquipment: ['Motor Test Stand', 'Hour Meter', 'Brush Wear Gauge'],
        testEnvironment: {
            temperature: '23°C ± 5°C'
        }
    },
    {
        testId: 'TEST-006',
        testName: 'Brushless Motor Life Test',
        testType: 'Reliability',
        testStandard: 'Internal Test Protocol ITP-MOT-002',
        linkedFmeaIds: ['FMEA-009'],
        linkedBomIds: ['6'],
        objectiveStatement: 'Verify brushless DC motor operates for minimum 200 hours without failure',
        testMethod: 'Run motor continuously at rated speed (15000 RPM) for 200 hours, monitor performance',
        sampleSize: 5,
        testDuration: '200 hours (8.3 days)',
        testEquipment: ['Motor Test Stand', 'Hour Meter', 'Vibration Monitor', 'Noise Meter'],
        testEnvironment: {
            temperature: '23°C ± 5°C'
        }
    },
    {
        testId: 'TEST-007',
        testName: 'Battery Contact Corrosion Test',
        testType: 'Environmental',
        testStandard: 'ASTM B117 (Modified)',
        linkedFmeaIds: ['FMEA-004'],
        linkedBomIds: ['4', '5'],
        objectiveStatement: 'Verify nickel-plated contacts resist corrosion in salt spray environment',
        testMethod: '48-hour salt spray exposure per ASTM B117, followed by contact resistance measurement',
        sampleSize: 6,
        testDuration: '48 hours + evaluation',
        testEquipment: ['Salt Spray Chamber', 'Ohmmeter (4-wire)', 'Microscope'],
        testEnvironment: {
            temperature: '35°C',
            humidity: 'Salt fog atmosphere, 5% NaCl'
        }
    },
    {
        testId: 'TEST-008',
        testName: 'Food Contact Material Compliance (FDA)',
        testType: 'Regulatory',
        testStandard: 'FDA CFR 177.2600 (Viton), 177.2750 (304 SS)',
        linkedFmeaIds: ['FMEA-015'],
        linkedBomIds: ['13', '14', '16'],
        objectiveStatement: 'Verify food-contact materials comply with FDA regulations for food safety',
        testMethod: 'Material certification review + XRF verification of 304 SS, Viton FDA compliance cert',
        sampleSize: 3,
        testDuration: '2 hours (documentation review + testing)',
        testEquipment: ['XRF Analyzer', 'Material Certificates'],
        testEnvironment: {
            temperature: 'Lab ambient'
        }
    },
    {
        testId: 'TEST-009',
        testName: 'Froth Performance Test',
        testType: 'Performance',
        testStandard: 'Customer Requirement Spec CRS-001',
        linkedFmeaIds: ['FMEA-014'],
        linkedBomIds: ['14'],
        objectiveStatement: 'Verify whisk froths 150ml milk in ≤30 seconds with foam volume ≥200ml',
        testMethod: 'Froth 150ml whole milk at room temperature, measure time to achieve stable foam and volume',
        sampleSize: 10,
        testDuration: '5 minutes per sample',
        testEquipment: ['Graduated Cylinder', 'Stopwatch', 'Thermometer'],
        testEnvironment: {
            temperature: '20-25°C',
            other: 'Whole milk (3.5% fat), room temperature'
        }
    },
    {
        testId: 'TEST-010',
        testName: 'Motor Noise Level Test',
        testType: 'Performance',
        testStandard: 'ISO 3744',
        linkedFmeaIds: ['FMEA-010'],
        linkedBomIds: ['6', '7'],
        objectiveStatement: 'Verify motor noise level ≤70 dB(A) at 1 meter distance during operation',
        testMethod: 'Measure sound pressure level at 1m in semi-anechoic chamber while motor runs at rated speed',
        sampleSize: 5,
        testDuration: '30 minutes per sample',
        testEquipment: ['Sound Level Meter (Type 1)', 'Semi-Anechoic Chamber', 'Tachometer'],
        testEnvironment: {
            temperature: '23°C ± 2°C',
            other: 'Background noise <40 dB(A)'
        }
    },
    {
        testId: 'TEST-011',
        testName: 'Shaft-Motor Press-Fit Pull Test',
        testType: 'Reliability',
        testStandard: 'Internal Test Protocol ITP-ASM-001',
        linkedFmeaIds: ['FMEA-013'],
        linkedBomIds: ['13'],
        objectiveStatement: 'Verify shaft-motor press-fit withstands ≥50N axial pull force',
        testMethod: 'Apply axial tensile force to shaft until slip or failure. Record maximum force.',
        sampleSize: 30,
        testDuration: '2 minutes per sample',
        testEquipment: ['Universal Testing Machine (UTM)', 'Force Gauge'],
        testEnvironment: {
            temperature: '23°C ± 5°C'
        }
    },
    {
        testId: 'TEST-012',
        testName: 'Electrical Safety Test (Leakage Current)',
        testType: 'Safety',
        testStandard: 'IEC 60335-1',
        linkedFmeaIds: ['FMEA-007'],
        linkedBomIds: ['11', '12'],
        objectiveStatement: 'Verify electrical leakage current <0.5mA during normal operation',
        testMethod: 'Measure leakage current between live parts and exposed metal parts at rated voltage',
        sampleSize: 10,
        testDuration: '15 minutes per sample',
        testEquipment: ['Leakage Current Tester', 'High-Resolution Ammeter'],
        testEnvironment: {
            temperature: '23°C ± 5°C',
            humidity: '50% ± 20%'
        }
    }
];

// ============================================
// TEST PARAMETERS
// ============================================

export const testParameters: TestParameter[] = [
    // TEST-001: Drop Test
    { parameterId: 'PARAM-001-01', testId: 'TEST-001', parameterName: 'Drop Height', unit: 'm', nominalValue: 1.0, lowerLimit: 0.95, upperLimit: 1.05, criticalParameter: true },
    { parameterId: 'PARAM-001-02', testId: 'TEST-001', parameterName: 'Number of Drops', unit: 'count', nominalValue: 6, lowerLimit: 6, upperLimit: 6, criticalParameter: true },
    { parameterId: 'PARAM-001-03', testId: 'TEST-001', parameterName: 'Surface Hardness', unit: 'Shore D', nominalValue: 'Concrete', lowerLimit: 'Concrete', upperLimit: 'Concrete', criticalParameter: true },

    // TEST-002: Seal Leak Test
    { parameterId: 'PARAM-002-01', testId: 'TEST-002', parameterName: 'Water Temperature', unit: '°C', nominalValue: 80, lowerLimit: 78, upperLimit: 82, criticalParameter: true },
    { parameterId: 'PARAM-002-02', testId: 'TEST-002', parameterName: 'Test Duration', unit: 'minutes', nominalValue: 30, lowerLimit: 30, upperLimit: 30, criticalParameter: true },
    { parameterId: 'PARAM-002-03', testId: 'TEST-002', parameterName: 'Motor Speed', unit: 'RPM', nominalValue: 15000, lowerLimit: 14500, upperLimit: 15500, criticalParameter: false },

    // TEST-003: Wire Flex
    { parameterId: 'PARAM-003-01', testId: 'TEST-003', parameterName: 'Flex Cycles', unit: 'cycles', nominalValue: 10000, lowerLimit: 10000, upperLimit: 10000, criticalParameter: true },
    { parameterId: 'PARAM-003-02', testId: 'TEST-003', parameterName: 'Flex Angle', unit: 'degrees', nominalValue: 90, lowerLimit: 85, upperLimit: 95, criticalParameter: false },
    { parameterId: 'PARAM-003-03', testId: 'TEST-003', parameterName: 'Cycle Rate', unit: 'cycles/min', nominalValue: 30, lowerLimit: 25, upperLimit: 35, criticalParameter: false },

    // TEST-004: IPX4 Test
    { parameterId: 'PARAM-004-01', testId: 'TEST-004', parameterName: 'Spray Duration', unit: 'minutes', nominalValue: 5, lowerLimit: 5, upperLimit: 10, criticalParameter: true },
    { parameterId: 'PARAM-004-02', testId: 'TEST-004', parameterName: 'Water Flow Rate', unit: 'L/min', nominalValue: 10, lowerLimit: 9, upperLimit: 11, criticalParameter: false },

    // TEST-005: Brush Motor Life
    { parameterId: 'PARAM-005-01', testId: 'TEST-005', parameterName: 'Test Duration', unit: 'hours', nominalValue: 100, lowerLimit: 100, upperLimit: 100, criticalParameter: true },
    { parameterId: 'PARAM-005-02', testId: 'TEST-005', parameterName: 'Motor Speed', unit: 'RPM', nominalValue: 12000, lowerLimit: 11500, upperLimit: 12500, criticalParameter: true },

    // TEST-006: Brushless Motor Life
    { parameterId: 'PARAM-006-01', testId: 'TEST-006', parameterName: 'Test Duration', unit: 'hours', nominalValue: 200, lowerLimit: 200, upperLimit: 200, criticalParameter: true },
    { parameterId: 'PARAM-006-02', testId: 'TEST-006', parameterName: 'Motor Speed', unit: 'RPM', nominalValue: 15000, lowerLimit: 14500, upperLimit: 15500, criticalParameter: true },
    { parameterId: 'PARAM-006-03', testId: 'TEST-006', parameterName: 'Noise Level', unit: 'dB(A)', nominalValue: 55, lowerLimit: 0, upperLimit: 70, criticalParameter: false },

    // TEST-007: Corrosion
    { parameterId: 'PARAM-007-01', testId: 'TEST-007', parameterName: 'Salt Spray Duration', unit: 'hours', nominalValue: 48, lowerLimit: 48, upperLimit: 48, criticalParameter: true },
    { parameterId: 'PARAM-007-02', testId: 'TEST-007', parameterName: 'Contact Resistance', unit: 'Ω', nominalValue: 0.1, lowerLimit: 0, upperLimit: 1.0, criticalParameter: true },

    // TEST-009: Froth Performance
    { parameterId: 'PARAM-009-01', testId: 'TEST-009', parameterName: 'Froth Time', unit: 'seconds', nominalValue: 25, lowerLimit: 0, upperLimit: 30, criticalParameter: true },
    { parameterId: 'PARAM-009-02', testId: 'TEST-009', parameterName: 'Foam Volume', unit: 'ml', nominalValue: 220, lowerLimit: 200, upperLimit: 300, criticalParameter: true },

    // TEST-010: Noise
    { parameterId: 'PARAM-010-01', testId: 'TEST-010', parameterName: 'Sound Level', unit: 'dB(A)', nominalValue: 60, lowerLimit: 0, upperLimit: 70, criticalParameter: true },

    // TEST-011: Pull Test
    { parameterId: 'PARAM-011-01', testId: 'TEST-011', parameterName: 'Pull Force', unit: 'N', nominalValue: 70, lowerLimit: 50, upperLimit: 200, criticalParameter: true },

    // TEST-012: Electrical Safety
    { parameterId: 'PARAM-012-01', testId: 'TEST-012', parameterName: 'Leakage Current', unit: 'mA', nominalValue: 0.2, lowerLimit: 0, upperLimit: 0.5, criticalParameter: true },
];

// ============================================
// PASS/FAIL CRITERIA
// ============================================

export const passFailCriteria: PassFailCriteria[] = [
    // TEST-001: Drop Test
    {
        criteriaId: 'CRIT-001-01',
        testId: 'TEST-001',
        description: 'No cracks or fractures in housing',
        acceptanceCriteria: 'Visual inspection shows no cracks, fractures, or deformation after all 6 drops',
        rejectCriteria: 'Any visible crack, fracture, or permanent deformation in housing'
    },
    {
        criteriaId: 'CRIT-001-02',
        testId: 'TEST-001',
        description: 'Device remains functional',
        acceptanceCriteria: 'Motor operates and switch functions normally after drop test',
        rejectCriteria: 'Motor fails to operate or switch is damaged'
    },

    // TEST-002: Seal Leak
    {
        criteriaId: 'CRIT-002-01',
        testId: 'TEST-002',
        description: 'No liquid ingress',
        acceptanceCriteria: 'No liquid detected inside motor housing after test. Tissue paper test shows dry.',
        rejectCriteria: 'Any liquid ingress detected visually or with moisture indicator',
        quantitativeLimit: { parameter: 'Liquid Ingress', operator: '==', value: 0 }
    },

    // TEST-003: Wire Flex
    {
        criteriaId: 'CRIT-003-01',
        testId: 'TEST-003',
        description: 'Wire continuity maintained',
        acceptanceCriteria: 'Electrical continuity <1Ω maintained for all 10,000 cycles',
        rejectCriteria: 'Wire break (open circuit) or resistance >10Ω',
        quantitativeLimit: { parameter: 'Resistance', operator: '<', value: 1.0 }
    },

    // TEST-004: IPX4
    {
        criteriaId: 'CRIT-004-01',
        testId: 'TEST-004',
        description: 'No water ingress in switch',
        acceptanceCriteria: 'No water inside switch housing, switch operates normally after drying',
        rejectCriteria: 'Water ingress detected or switch failure'
    },

    // TEST-005: Brush Motor Life
    {
        criteriaId: 'CRIT-005-01',
        testId: 'TEST-005',
        description: 'Motor operates for 100 hours',
        acceptanceCriteria: 'Motor runs continuously for 100 hours without failure. Speed within ±10% of rated.',
        rejectCriteria: 'Motor fails before 100 hours or speed drops >10%',
        quantitativeLimit: { parameter: 'Operating Hours', operator: '>=', value: 100 }
    },

    // TEST-006: Brushless Motor Life
    {
        criteriaId: 'CRIT-006-01',
        testId: 'TEST-006',
        description: 'Motor operates for 200 hours',
        acceptanceCriteria: 'Motor runs continuously for 200 hours without failure. Performance unchanged.',
        rejectCriteria: 'Motor fails before 200 hours or performance degrades',
        quantitativeLimit: { parameter: 'Operating Hours', operator: '>=', value: 200 }
    },
    {
        criteriaId: 'CRIT-006-02',
        testId: 'TEST-006',
        description: 'Noise level remains acceptable',
        acceptanceCriteria: 'Noise level ≤70 dB(A) throughout test',
        rejectCriteria: 'Noise exceeds 70 dB(A)',
        quantitativeLimit: { parameter: 'Noise', operator: '<=', value: 70 }
    },

    // TEST-007: Corrosion
    {
        criteriaId: 'CRIT-007-01',
        testId: 'TEST-007',
        description: 'Minimal corrosion after salt spray',
        acceptanceCriteria: 'Contact resistance <1Ω after 48h salt spray. No visible pitting.',
        rejectCriteria: 'Contact resistance >1Ω or visible pitting corrosion',
        quantitativeLimit: { parameter: 'Contact Resistance', operator: '<=', value: 1.0 }
    },

    // TEST-008: FDA Compliance
    {
        criteriaId: 'CRIT-008-01',
        testId: 'TEST-008',
        description: '304 SS verified',
        acceptanceCriteria: 'XRF confirms 304 stainless steel composition per ASTM A240',
        rejectCriteria: 'Material does not meet 304 SS specification'
    },
    {
        criteriaId: 'CRIT-008-02',
        testId: 'TEST-008',
        description: 'Viton FDA compliant',
        acceptanceCriteria: 'Supplier certificate confirms Viton FKM complies with FDA CFR 177.2600',
        rejectCriteria: 'No valid FDA certification for Viton material'
    },

    // TEST-009: Froth Performance
    {
        criteriaId: 'CRIT-009-01',
        testId: 'TEST-009',
        description: 'Froth time ≤30 seconds',
        acceptanceCriteria: 'Stable foam achieved in ≤30 seconds',
        rejectCriteria: 'Requires >30 seconds to achieve foam',
        quantitativeLimit: { parameter: 'Froth Time', operator: '<=', value: 30 }
    },
    {
        criteriaId: 'CRIT-009-02',
        testId: 'TEST-009',
        description: 'Foam volume ≥200ml',
        acceptanceCriteria: 'Final foam volume ≥200ml',
        rejectCriteria: 'Foam volume <200ml',
        quantitativeLimit: { parameter: 'Foam Volume', operator: '>=', value: 200 }
    },

    // TEST-010: Noise
    {
        criteriaId: 'CRIT-010-01',
        testId: 'TEST-010',
        description: 'Noise ≤70 dB(A)',
        acceptanceCriteria: 'Sound level ≤70 dB(A) at 1m distance',
        rejectCriteria: 'Sound level >70 dB(A)',
        quantitativeLimit: { parameter: 'Sound Level', operator: '<=', value: 70 }
    },

    // TEST-011: Pull Test
    {
        criteriaId: 'CRIT-011-01',
        testId: 'TEST-011',
        description: 'Pull force ≥50N',
        acceptanceCriteria: 'Shaft withstands ≥50N pull without slipping',
        rejectCriteria: 'Shaft slips at <50N',
        quantitativeLimit: { parameter: 'Pull Force', operator: '>=', value: 50 }
    },

    // TEST-012: Electrical Safety
    {
        criteriaId: 'CRIT-012-01',
        testId: 'TEST-012',
        description: 'Leakage current <0.5mA',
        acceptanceCriteria: 'Leakage current measured <0.5mA',
        rejectCriteria: 'Leakage current ≥0.5mA',
        quantitativeLimit: { parameter: 'Leakage Current', operator: '<', value: 0.5 }
    }
];

// ============================================
// TEST RESULTS - 17 ITERATIONS
// ============================================

export const testResults: TestResult[] = [
    // ITERATION 1 - Baseline (many failures expected)
    {
        resultId: 'RES-001-I1',
        testId: 'TEST-001',
        iterationNumber: 1,
        iterationDate: '2025-08-01',
        testDate: '2025-08-05',
        testedBy: 'WK',
        witnessedBy: 'MC',
        sampleId: 'S001-I1-01',
        status: 'Fail',
        measurements: [
            { parameterId: 'PARAM-001-01', measuredValue: 1.0, passFail: 'Pass' },
            { parameterId: 'PARAM-001-02', measuredValue: 6, passFail: 'Pass' }
        ],
        overallResult: 'Fail',
        failureMode: 'Housing cracked on 3rd drop (top corner)',
        rootCause: 'Standard ABS material insufficient impact strength',
        correctiveAction: 'ECN-2025-004: Upgrade to high-impact ABS',
        projectName: 'Aerator Pro',
        notes: 'Baseline test - expected failure. Need material upgrade.'
    },

    // ============================================
    // ALPHA BUILD FAILURES (I1-I5) - Early development
    // ============================================

    // Drop test - I2 retest still fails (standard ABS)
    {
        resultId: 'RES-001-I2',
        testId: 'TEST-001',
        iterationNumber: 2,
        iterationDate: '2025-08-10',
        testDate: '2025-08-12',
        testedBy: 'WK',
        witnessedBy: 'MC',
        sampleId: 'S001-I2-01',
        status: 'Fail',
        measurements: [
            { parameterId: 'PARAM-001-01', measuredValue: 1.0, passFail: 'Pass' },
            { parameterId: 'PARAM-001-02', measuredValue: 6, passFail: 'Pass' }
        ],
        overallResult: 'Fail',
        failureMode: 'Housing cracked on 4th drop (bottom edge), worse than I1',
        rootCause: 'Standard ABS still insufficient - sharp internal corners act as stress risers',
        correctiveAction: 'ECN-2025-004: Add internal fillet radii AND upgrade material to HI-ABS',
        projectName: 'Aerator Pro',
        notes: 'Alpha build retest. No improvement with current material. Need material upgrade.'
    },

    // Wire flex - I1 baseline (before strain relief)
    {
        resultId: 'RES-003-I1',
        testId: 'TEST-003',
        iterationNumber: 1,
        iterationDate: '2025-08-01',
        testDate: '2025-08-07',
        testedBy: 'WK',
        witnessedBy: 'MC',
        sampleId: 'S003-I1-01',
        status: 'Fail',
        measurements: [
            { parameterId: 'PARAM-003-01', measuredValue: 3200, passFail: 'Fail' },
            { parameterId: 'PARAM-003-02', measuredValue: 90, passFail: 'Pass' },
            { parameterId: 'PARAM-003-03', measuredValue: 30, passFail: 'Pass' }
        ],
        overallResult: 'Fail',
        failureMode: 'Wire broke at housing exit after 3,200 cycles (spec: 10,000)',
        rootCause: 'No strain relief at wire exit, sharp edge cuts insulation under flex',
        correctiveAction: 'ECN-2025-001: Add molded strain relief feature to housing',
        projectName: 'Aerator Pro',
        notes: 'Critical failure. Wire broke at 32% of required life. Strain relief mandatory.'
    },

    // Battery contact corrosion - I1 baseline (bare steel)
    {
        resultId: 'RES-007-I1',
        testId: 'TEST-007',
        iterationNumber: 1,
        iterationDate: '2025-08-01',
        testDate: '2025-08-10',
        testedBy: 'JS',
        witnessedBy: 'WK',
        sampleId: 'S007-I1-01',
        status: 'Fail',
        measurements: [
            { parameterId: 'PARAM-007-01', measuredValue: 48, passFail: 'Pass' },
            { parameterId: 'PARAM-007-02', measuredValue: 4.7, passFail: 'Fail' }
        ],
        overallResult: 'Fail',
        failureMode: 'Heavy rust/corrosion on bare steel contacts. Resistance 4.7Ω (spec: <1Ω)',
        rootCause: 'Bare steel has no corrosion protection. Oxidizes rapidly in humid/salt environment',
        correctiveAction: 'ECN-2025-002: Nickel plate all battery contacts (5-10 μm)',
        projectName: 'Aerator Pro',
        notes: 'Complete failure. Contacts corroded badly. Must add plating.'
    },

    // Electrical safety - I1 baseline
    {
        resultId: 'RES-012-I1',
        testId: 'TEST-012',
        iterationNumber: 1,
        iterationDate: '2025-08-01',
        testDate: '2025-08-09',
        testedBy: 'JS',
        witnessedBy: 'WK',
        sampleId: 'S012-I1-01',
        status: 'Conditional',
        measurements: [
            { parameterId: 'PARAM-012-01', measuredValue: 0.42, passFail: 'Pass' }
        ],
        overallResult: 'Conditional',
        failureMode: 'Leakage current 0.42mA - passes but marginal (limit 0.5mA)',
        rootCause: 'No conformal coating on PCB, moisture paths exist',
        correctiveAction: 'Monitor in subsequent builds. Add conformal coating if trend worsens.',
        projectName: 'Aerator Pro',
        notes: 'Conditional pass. Only 16% margin. Needs improvement for production.'
    },

    // IPX4 - I1 baseline (before gasket improvement)
    {
        resultId: 'RES-004-I1',
        testId: 'TEST-004',
        iterationNumber: 1,
        iterationDate: '2025-08-01',
        testDate: '2025-08-08',
        testedBy: 'JS',
        witnessedBy: 'WK',
        sampleId: 'S004-I1-01',
        status: 'Fail',
        measurements: [
            { parameterId: 'PARAM-004-01', measuredValue: 5, passFail: 'Pass' },
            { parameterId: 'PARAM-004-02', measuredValue: 10, passFail: 'Pass' }
        ],
        overallResult: 'Fail',
        failureMode: 'Water ingress detected in switch cavity after 3 minutes of spray',
        rootCause: 'Silicone gasket too soft, poor compression recovery after initial squeeze',
        correctiveAction: 'ECN-2025-006: Change switch gasket from silicone to TPE for better compression set',
        projectName: 'Aerator Pro',
        notes: 'IPX4 FAILED. Switch gasket is inadequate. Safety hazard if water reaches motor.'
    },

    // Brushed motor life - I1 baseline (early brush wear)
    {
        resultId: 'RES-005-I1',
        testId: 'TEST-005',
        iterationNumber: 1,
        iterationDate: '2025-08-01',
        testDate: '2025-08-15',
        testedBy: 'WK',
        witnessedBy: 'MC',
        sampleId: 'S005-I1-01',
        status: 'Conditional',
        measurements: [
            { parameterId: 'PARAM-005-01', measuredValue: 100, passFail: 'Pass' },
            { parameterId: 'PARAM-005-02', measuredValue: 11800, passFail: 'Pass' }
        ],
        overallResult: 'Conditional',
        failureMode: 'Motor survived 100h but brush wear at 85% - very near end-of-life',
        rootCause: 'Brushed DC motor inherently has limited brush life in continuous-use application',
        correctiveAction: 'ECN-2025-011: Evaluate brushless motor upgrade for longer life',
        projectName: 'Aerator Pro',
        notes: 'Conditional pass. Brushes nearly gone at 100h. Not acceptable for production.'
    },

    // Froth performance - I1 baseline (original small whisk)
    {
        resultId: 'RES-009-I1',
        testId: 'TEST-009',
        iterationNumber: 1,
        iterationDate: '2025-08-01',
        testDate: '2025-08-06',
        testedBy: 'WK',
        witnessedBy: 'MC',
        sampleId: 'S009-I1-01',
        status: 'Fail',
        measurements: [
            { parameterId: 'PARAM-009-01', measuredValue: 52, passFail: 'Fail' },
            { parameterId: 'PARAM-009-02', measuredValue: 165, passFail: 'Fail' }
        ],
        overallResult: 'Fail',
        failureMode: 'Froth time 52s (spec: ≤30s) and foam volume 165ml (spec: ≥200ml)',
        rootCause: '20mm prototype whisk too small, low RPM brushed motor insufficient aeration',
        correctiveAction: 'Plan larger whisk after motor upgrade',
        projectName: 'Aerator Pro',
        notes: 'Alpha baseline. Performance far below target. Motor+whisk redesign needed.'
    },

    // Noise test - I1 baseline (brushed motor + steel bearings)
    {
        resultId: 'RES-010-I1',
        testId: 'TEST-010',
        iterationNumber: 1,
        iterationDate: '2025-08-01',
        testDate: '2025-08-06',
        testedBy: 'JS',
        witnessedBy: 'WK',
        sampleId: 'S010-I1-01',
        status: 'Fail',
        measurements: [
            { parameterId: 'PARAM-010-01', measuredValue: 78, passFail: 'Fail' }
        ],
        overallResult: 'Fail',
        failureMode: 'Noise level 78 dB(A) far exceeds 70 dB limit',
        rootCause: 'Brushed motor with steel bearings generates excessive brush and bearing noise',
        correctiveAction: 'ECN-2025-009: Upgrade to ceramic bearings; ECN-2025-011: Brushless motor',
        projectName: 'Aerator Pro',
        notes: 'Alpha build very noisy. 8 dB over limit. Major motor redesign required.'
    },

    // Pull test - I1 baseline (manual press-fit, no control)
    {
        resultId: 'RES-011-I1',
        testId: 'TEST-011',
        iterationNumber: 1,
        iterationDate: '2025-08-01',
        testDate: '2025-08-07',
        testedBy: 'WK',
        witnessedBy: 'MC',
        sampleId: 'S011-I1-01',
        status: 'Fail',
        measurements: [
            { parameterId: 'PARAM-011-01', measuredValue: 38, passFail: 'Fail' }
        ],
        overallResult: 'Fail',
        failureMode: 'Shaft slipped at 38N (spec: ≥50N). Motor spins but whisk does not',
        rootCause: 'Manual press-fit inconsistent. No force monitoring during assembly',
        correctiveAction: 'ECN-2025-012: Develop controlled press-fit procedure with force monitoring',
        projectName: 'Aerator Pro',
        notes: 'Alpha hand-assembly. Press-fit way too weak. Process control needed.'
    },

    // ============================================
    // BETA 1 BUILD (I6-I10) - Improvements shown
    // ============================================

    // IPX4 - I4 retest (silicone gasket still marginal)
    {
        resultId: 'RES-004-I4',
        testId: 'TEST-004',
        iterationNumber: 4,
        iterationDate: '2025-09-01',
        testDate: '2025-09-05',
        testedBy: 'JS',
        witnessedBy: 'MC',
        sampleId: 'S004-I4-01',
        status: 'Conditional',
        measurements: [
            { parameterId: 'PARAM-004-01', measuredValue: 5, passFail: 'Pass' },
            { parameterId: 'PARAM-004-02', measuredValue: 10, passFail: 'Pass' }
        ],
        overallResult: 'Conditional',
        failureMode: 'Minor moisture traces inside switch cavity, no short circuit',
        rootCause: 'Silicone gasket better but still shows compression set after 1 week',
        correctiveAction: 'ECN-2025-006: Proceed with TPE gasket swap in I7',
        projectName: 'Aerator Pro',
        notes: 'Improved from I1 but still not robust. TPE swap planned.'
    },

    // Pull test retest - I10 (still manual but improved)
    {
        resultId: 'RES-011-I10',
        testId: 'TEST-011',
        iterationNumber: 10,
        iterationDate: '2025-10-30',
        testDate: '2025-11-02',
        testedBy: 'WK',
        witnessedBy: 'JS',
        sampleId: 'S011-I10-01',
        status: 'Conditional',
        measurements: [
            { parameterId: 'PARAM-011-01', measuredValue: 52, passFail: 'Pass' }
        ],
        overallResult: 'Conditional',
        failureMode: 'Pull force 52N passes but only 4% margin. Inconsistent manual process',
        rootCause: 'Manual press-fit has high variation. Some units may not reach 50N',
        correctiveAction: 'ECN-2025-012: Implement pneumatic press with 500-800N force monitoring',
        projectName: 'Aerator Pro',
        notes: 'Marginal pass. Cpk=0.8 (need >1.33). Process control equipment on order.'
    },

    // Electrical safety - I6 improved
    {
        resultId: 'RES-012-I6',
        testId: 'TEST-012',
        iterationNumber: 6,
        iterationDate: '2025-09-25',
        testDate: '2025-09-28',
        testedBy: 'JS',
        witnessedBy: 'MC',
        sampleId: 'S012-I6-01',
        status: 'Pass',
        measurements: [
            { parameterId: 'PARAM-012-01', measuredValue: 0.31, passFail: 'Pass' }
        ],
        overallResult: 'Pass',
        projectName: 'Aerator Pro',
        notes: 'Improved with better insulation on wiring. 38% margin now.'
    },

    // ============================================
    // BETA 2 BUILD (I11-I14) - Convergence
    // ============================================

    // Drop test reconfirmation - I12
    {
        resultId: 'RES-001-I12',
        testId: 'TEST-001',
        iterationNumber: 12,
        iterationDate: '2025-10-25',
        testDate: '2025-10-28',
        testedBy: 'WK',
        witnessedBy: 'MC',
        sampleId: 'S001-I12-01',
        status: 'Pass',
        measurements: [
            { parameterId: 'PARAM-001-01', measuredValue: 1.0, passFail: 'Pass' },
            { parameterId: 'PARAM-001-02', measuredValue: 6, passFail: 'Pass' }
        ],
        overallResult: 'Pass',
        projectName: 'Aerator Pro',
        notes: 'Beta 2 build. Drop test confirmed with new brushless motor. No issues.'
    },

    // Seal test reconfirmation - I13
    {
        resultId: 'RES-002-I13',
        testId: 'TEST-002',
        iterationNumber: 13,
        iterationDate: '2025-11-05',
        testDate: '2025-11-08',
        testedBy: 'JS',
        witnessedBy: 'WK',
        sampleId: 'S002-I13-01',
        status: 'Pass',
        measurements: [
            { parameterId: 'PARAM-002-01', measuredValue: 80, passFail: 'Pass' },
            { parameterId: 'PARAM-002-02', measuredValue: 30, passFail: 'Pass' },
            { parameterId: 'PARAM-002-03', measuredValue: 15000, passFail: 'Pass' }
        ],
        overallResult: 'Pass',
        projectName: 'Aerator Pro',
        notes: 'Beta 2 full DVP revalidation. Dual seal + Viton. All 10 samples passed.'
    },

    // Performance reconfirm - I12
    {
        resultId: 'RES-009-I12',
        testId: 'TEST-009',
        iterationNumber: 12,
        iterationDate: '2025-10-25',
        testDate: '2025-10-29',
        testedBy: 'WK',
        witnessedBy: 'MC',
        sampleId: 'S009-I12-01',
        status: 'Pass',
        measurements: [
            { parameterId: 'PARAM-009-01', measuredValue: 23, passFail: 'Pass' },
            { parameterId: 'PARAM-009-02', measuredValue: 230, passFail: 'Pass' }
        ],
        overallResult: 'Pass',
        projectName: 'Aerator Pro',
        notes: 'Brushless motor + 35mm whisk = excellent performance. 23s / 230ml.'
    },

    // Drop test continued through iterations
    {
        resultId: 'RES-001-I5',
        testId: 'TEST-001',
        iterationNumber: 5,
        iterationDate: '2025-09-15',
        testDate: '2025-09-18',
        testedBy: 'WK',
        witnessedBy: 'JS',
        sampleId: 'S001-I5-01',
        status: 'Pass',
        measurements: [
            { parameterId: 'PARAM-001-01', measuredValue: 1.0, passFail: 'Pass' },
            { parameterId: 'PARAM-001-02', measuredValue: 6, passFail: 'Pass' }
        ],
        overallResult: 'Pass',
        projectName: 'Aerator Pro',
        notes: 'High-impact ABS material - all 6 drops passed with no damage. Significant improvement!'
    },

    // Seal Test - CRITICAL
    {
        resultId: 'RES-002-I1',
        testId: 'TEST-002',
        iterationNumber: 1,
        iterationDate: '2025-08-01',
        testDate: '2025-08-08',
        testedBy: 'WK',
        witnessedBy: 'MC',
        sampleId: 'S002-I1-01',
        status: 'Fail',
        measurements: [
            { parameterId: 'PARAM-002-01', measuredValue: 80, passFail: 'Pass' },
            { parameterId: 'PARAM-002-02', measuredValue: 30, passFail: 'Pass' },
            { parameterId: 'PARAM-002-03', measuredValue: 12000, passFail: 'Pass' }
        ],
        overallResult: 'Fail',
        failureMode: 'Liquid ingress detected - seal failed completely',
        rootCause: 'Silicone seal degraded at 80°C - temp rating insufficient',
        correctiveAction: 'ECN-2025-005: Change to Viton FKM seal (200°C rating)',
        projectName: 'Aerator Pro',
        notes: 'CRITICAL FAILURE - Product cannot be released with silicone seal'
    },

    {
        resultId: 'RES-002-I6',
        testId: 'TEST-002',
        iterationNumber: 6,
        iterationDate: '2025-09-25',
        testDate: '2025-09-27',
        testedBy: 'WK',
        witnessedBy: 'JS',
        sampleId: 'S002-I6-01',
        status: 'Pass',
        measurements: [
            { parameterId: 'PARAM-002-01', measuredValue: 80, passFail: 'Pass' },
            { parameterId: 'PARAM-002-02', measuredValue: 30, passFail: 'Pass' },
            { parameterId: 'PARAM-002-03', measuredValue: 15000, passFail: 'Pass' }
        ],
        overallResult: 'Pass',
        projectName: 'Aerator Pro',
        notes: 'Viton FKM seal - NO liquid ingress. Test passed! Critical issue resolved.'
    },

    {
        resultId: 'RES-002-I10',
        testId: 'TEST-002',
        iterationNumber: 10,
        iterationDate: '2025-10-30',
        testDate: '2025-11-01',
        testedBy: 'JS',
        witnessedBy: 'WK',
        sampleId: 'S002-I10-01',
        status: 'Pass',
        measurements: [
            { parameterId: 'PARAM-002-01', measuredValue: 80, passFail: 'Pass' },
            { parameterId: 'PARAM-002-02', measuredValue: 30, passFail: 'Pass' },
            { parameterId: 'PARAM-002-03', measuredValue: 15000, passFail: 'Pass' }
        ],
        overallResult: 'Pass',
        projectName: 'Aerator Pro',
        notes: 'Dual seal design (Viton + NBR backup) - enhanced reliability confirmed'
    },

    // Wire Flex Test
    {
        resultId: 'RES-003-I2',
        testId: 'TEST-003',
        iterationNumber: 2,
        iterationDate: '2025-08-10',
        testDate: '2025-08-12',
        testedBy: 'WK',
        witnessedBy: 'MC',
        sampleId: 'S003-I2-01',
        status: 'Pass',
        measurements: [
            { parameterId: 'PARAM-003-01', measuredValue: 10000, passFail: 'Pass' },
            { parameterId: 'PARAM-003-02', measuredValue: 90, passFail: 'Pass' },
            { parameterId: 'PARAM-003-03', measuredValue: 30, passFail: 'Pass' }
        ],
        overallResult: 'Pass',
        projectName: 'Aerator Pro',
        notes: 'Strain relief feature effective - no wire breaks after 10k cycles'
    },

    // IPX4 Test
    {
        resultId: 'RES-004-I7',
        testId: 'TEST-004',
        iterationNumber: 7,
        iterationDate: '2025-10-01',
        testDate: '2025-10-03',
        testedBy: 'JS',
        witnessedBy: 'WK',
        sampleId: 'S004-I7-01',
        status: 'Pass',
        measurements: [
            { parameterId: 'PARAM-004-01', measuredValue: 5, passFail: 'Pass' },
            { parameterId: 'PARAM-004-02', measuredValue: 10, passFail: 'Pass' }
        ],
        overallResult: 'Pass',
        projectName: 'Aerator Pro',
        notes: 'TPE gasket provides excellent seal - IPX4 passed'
    },

    // Brushed Motor Life (before upgrade)
    {
        resultId: 'RES-005-I8',
        testId: 'TEST-005',
        iterationNumber: 8,
        iterationDate: '2025-10-10',
        testDate: '2025-10-15',
        testedBy: 'WK',
        witnessedBy: 'JS',
        sampleId: 'S005-I8-01',
        status: 'Pass',
        measurements: [
            { parameterId: 'PARAM-005-01', measuredValue: 100, passFail: 'Pass' },
            { parameterId: 'PARAM-005-02', measuredValue: 12000, passFail: 'Pass' }
        ],
        overallResult: 'Pass',
        projectName: 'Aerator Pro',
        notes: '100h life test passed but brush wear at 70% - borderline. Brushless upgrade recommended.'
    },

    // Brushless Motor Life (after upgrade)
    {
        resultId: 'RES-006-I12',
        testId: 'TEST-006',
        iterationNumber: 12,
        iterationDate: '2025-10-25',
        testDate: '2025-11-05',
        testedBy: 'WK',
        witnessedBy: 'MC',
        sampleId: 'S006-I12-01',
        status: 'Pass',
        measurements: [
            { parameterId: 'PARAM-006-01', measuredValue: 200, passFail: 'Pass' },
            { parameterId: 'PARAM-006-02', measuredValue: 15000, passFail: 'Pass' },
            { parameterId: 'PARAM-006-03', measuredValue: 55, passFail: 'Pass' }
        ],
        overallResult: 'Pass',
        projectName: 'Aerator Pro',
        notes: 'Brushless motor - 200h test passed with no degradation. Noise only 55 dB - excellent!'
    },

    // Corrosion Test
    {
        resultId: 'RES-007-I3',
        testId: 'TEST-007',
        iterationNumber: 3,
        iterationDate: '2025-08-20',
        testDate: '2025-08-25',
        testedBy: 'JS',
        witnessedBy: 'WK',
        sampleId: 'S007-I3-01',
        status: 'Pass',
        measurements: [
            { parameterId: 'PARAM-007-01', measuredValue: 48, passFail: 'Pass' },
            { parameterId: 'PARAM-007-02', measuredValue: 0.15, passFail: 'Pass' }
        ],
        overallResult: 'Pass',
        projectName: 'Aerator Pro',
        notes: 'Nickel-plated contacts - excellent corrosion resistance. Resistance <0.2Ω after 48h'
    },

    // FDA Compliance
    {
        resultId: 'RES-008-I4',
        testId: 'TEST-008',
        iterationNumber: 4,
        iterationDate: '2025-09-01',
        testDate: '2025-09-03',
        testedBy: 'JS',
        witnessedBy: 'MC',
        sampleId: 'S008-I4-01',
        status: 'Pass',
        measurements: [],
        overallResult: 'Pass',
        projectName: 'Aerator Pro',
        notes: 'XRF confirmed 304 SS. Supplier certs for Viton FDA compliance verified. All materials compliant.'
    },

    // Froth Performance - 25mm whisk (before upgrade)
    {
        resultId: 'RES-009-I8',
        testId: 'TEST-009',
        iterationNumber: 8,
        iterationDate: '2025-10-10',
        testDate: '2025-10-12',
        testedBy: 'WK',
        witnessedBy: 'JS',
        sampleId: 'S009-I8-01',
        status: 'Fail',
        measurements: [
            { parameterId: 'PARAM-009-01', measuredValue: 45, passFail: 'Fail' },
            { parameterId: 'PARAM-009-02', measuredValue: 190, passFail: 'Fail' }
        ],
        overallResult: 'Fail',
        failureMode: 'Froth time too slow (45s), volume insufficient (190ml)',
        rootCause: '25mm whisk too small - insufficient aeration surface area',
        correctiveAction: 'ECN-2025-008: Increase whisk diameter to 35mm',
        projectName: 'Aerator Pro',
        notes: 'Performance below target - whisk upgrade needed'
    },

    // Froth Performance - 35mm whisk (after upgrade)
    {
        resultId: 'RES-009-I9',
        testId: 'TEST-009',
        iterationNumber: 9,
        iterationDate: '2025-10-18',
        testDate: '2025-10-20',
        testedBy: 'WK',
        witnessedBy: 'MC',
        sampleId: 'S009-I9-01',
        status: 'Pass',
        measurements: [
            { parameterId: 'PARAM-009-01', measuredValue: 25, passFail: 'Pass' },
            { parameterId: 'PARAM-009-02', measuredValue: 220, passFail: 'Pass' }
        ],
        overallResult: 'Pass',
        projectName: 'Aerator Pro',
        notes: '35mm whisk - froth time 25s (33% faster!), volume 220ml. Exceeds requirements!'
    },

    // Noise Test - Steel bearings (before upgrade)
    {
        resultId: 'RES-010-I10',
        testId: 'TEST-010',
        iterationNumber: 10,
        iterationDate: '2025-10-30',
        testDate: '2025-11-02',
        testedBy: 'JS',
        witnessedBy: 'WK',
        sampleId: 'S010-I10-01',
        status: 'Fail',
        measurements: [
            { parameterId: 'PARAM-010-01', measuredValue: 72, passFail: 'Fail' }
        ],
        overallResult: 'Fail',
        failureMode: 'Noise level 72 dB(A) exceeds 70 dB limit',
        rootCause: 'Steel ball bearings generate excessive noise',
        correctiveAction: 'ECN-2025-009: Upgrade to ceramic bearings',
        projectName: 'Aerator Pro',
        notes: 'Customer requirement: <70 dB. Need quieter bearings.'
    },

    // Noise Test - Ceramic bearings (after upgrade)
    {
        resultId: 'RES-010-I11',
        testId: 'TEST-010',
        iterationNumber: 11,
        iterationDate: '2025-11-05',
        testDate: '2025-11-07',
        testedBy: 'JS',
        witnessedBy: 'MC',
        sampleId: 'S010-I11-01',
        status: 'Pass',
        measurements: [
            { parameterId: 'PARAM-010-01', measuredValue: 55, passFail: 'Pass' }
        ],
        overallResult: 'Pass',
        projectName: 'Aerator Pro',
        notes: 'Ceramic bearings - noise only 55 dB! 40% quieter. Customer delighted.'
    },

    // Pull Test
    {
        resultId: 'RES-011-I15',
        testId: 'TEST-011',
        iterationNumber: 15,
        iterationDate: '2025-11-20',
        testDate: '2025-11-22',
        testedBy: 'WK',
        witnessedBy: 'JS',
        sampleId: 'S011-I15-01',
        status: 'Pass',
        measurements: [
            { parameterId: 'PARAM-011-01', measuredValue: 72, passFail: 'Pass' }
        ],
        overallResult: 'Pass',
        projectName: 'Aerator Pro',
        notes: 'Controlled press-fit process - pull force 72N (44% margin). Cpk=1.67'
    },

    // Electrical Safety
    {
        resultId: 'RES-012-I16',
        testId: 'TEST-012',
        iterationNumber: 16,
        iterationDate: '2025-12-01',
        testDate: '2025-12-03',
        testedBy: 'JS',
        witnessedBy: 'MC',
        sampleId: 'S012-I16-01',
        status: 'Pass',
        measurements: [
            { parameterId: 'PARAM-012-01', measuredValue: 0.18, passFail: 'Pass' }
        ],
        overallResult: 'Pass',
        notes: 'Leakage current 0.18mA - well below 0.5mA limit. IEC 60335 compliant.'
    },

    // FINAL VALIDATION - Iteration 17
    {
        resultId: 'RES-001-I17',
        testId: 'TEST-001',
        iterationNumber: 17,
        iterationDate: '2025-12-12',
        testDate: '2025-12-13',
        testedBy: 'WK',
        witnessedBy: 'MC',
        sampleId: 'S001-I17-FINAL',
        status: 'Pass',
        measurements: [
            { parameterId: 'PARAM-001-01', measuredValue: 1.0, passFail: 'Pass' },
            { parameterId: 'PARAM-001-02', measuredValue: 6, passFail: 'Pass' }
        ],
        overallResult: 'Pass',
        notes: 'FINAL VALIDATION - Drop test passed. Production release approved.'
    },

    {
        resultId: 'RES-002-I17',
        testId: 'TEST-002',
        iterationNumber: 17,
        iterationDate: '2025-12-12',
        testDate: '2025-12-14',
        testedBy: 'JS',
        witnessedBy: 'WK',
        sampleId: 'S002-I17-FINAL',
        status: 'Pass',
        measurements: [
            { parameterId: 'PARAM-002-01', measuredValue: 80, passFail: 'Pass' },
            { parameterId: 'PARAM-002-02', measuredValue: 30, passFail: 'Pass' },
            { parameterId: 'PARAM-002-03', measuredValue: 15000, passFail: 'Pass' }
        ],
        overallResult: 'Pass',
        notes: 'FINAL VALIDATION - Seal test passed. Zero defects in pilot build (50 units).'
    },

    {
        resultId: 'RES-009-I17',
        testId: 'TEST-009',
        iterationNumber: 17,
        iterationDate: '2025-12-12',
        testDate: '2025-12-14',
        testedBy: 'MC',
        witnessedBy: 'JS',
        sampleId: 'S009-I17-FINAL',
        status: 'Pass',
        measurements: [
            { parameterId: 'PARAM-009-01', measuredValue: 24, passFail: 'Pass' },
            { parameterId: 'PARAM-009-02', measuredValue: 225, passFail: 'Pass' }
        ],
        overallResult: 'Pass',
        notes: 'FINAL VALIDATION - Performance excellent. Froth time 24s, volume 225ml. Ready for production!'
    }
];

// Summary Statistics
export const dvprSummary = {
    totalTests: testSpecifications.length,
    totalParameters: testParameters.length,
    totalCriteria: passFailCriteria.length,
    totalResults: testResults.length,
    testsByType: {
        Reliability: testSpecifications.filter(t => t.testType === 'Reliability').length,
        Performance: testSpecifications.filter(t => t.testType === 'Performance').length,
        Safety: testSpecifications.filter(t => t.testType === 'Safety').length,
        Environmental: testSpecifications.filter(t => t.testType === 'Environmental').length,
        Regulatory: testSpecifications.filter(t => t.testType === 'Regulatory').length,
        Material: testSpecifications.filter(t => t.testType === 'Material').length
    },
    resultsByIteration: Array.from({ length: 17 }, (_, i) => ({
        iteration: i + 1,
        testsRun: testResults.filter(r => r.iterationNumber === i + 1).length,
        passed: testResults.filter(r => r.iterationNumber === i + 1 && r.overallResult === 'Pass').length,
        failed: testResults.filter(r => r.iterationNumber === i + 1 && r.overallResult === 'Fail').length
    })),
    finalValidationStatus: {
        iteration17Passed: testResults.filter(r => r.iterationNumber === 17 && r.overallResult === 'Pass').length,
        readyForProduction: true
    }
};
